import React, { useContext, useState, useEffect } from 'react';
import ModalContext from '../../../contexts/ModalContext';
import {
  ModalContainer,
  Title,
  Subtitle,
  TableTitle,
  Table,
  Th,
  Td,
  ItemRow,
  MotivoInelegibilidade,
  DescricaoRiscada,
  BadgesRow,
  BadgePendencia,
  ValorFixo,
  Legenda,
  LegendaItem,
  LegendaDot,
  CheckboxInput,
  QtdInput,
  ValorInput,
  ActionsContainer,
  CancelButton,
  ConfirmButton
} from './style';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../services/ApiService';
import { stripHtml } from '../../../utils/stripHtml';
import { FeedbackContext } from '../../../contexts/FeedbackContext';

// O DTO do fiscal valida no máximo 4 casas decimais em valorUnitario/valorTotal.
const round4 = (value: number) => Math.round(value * 10000) / 10000;

const GenerateOrderInvoiceModal: React.FC = () => {
  const { selectedSteelOrder, setModalActive } = useContext(ModalContext);
  const { setFeedback } = useContext(FeedbackContext);
  const navigate = useNavigate();

  const [items, setItems] = useState<any[]>([]);

  useEffect(() => {
    const fetchEligibleItems = async () => {
      if (selectedSteelOrder) {
        try {
          const { data } = await ApiService.fiscal.getEligibleNfeItems(selectedSteelOrder.id);
          if (data && Array.isArray(data)) {
            setItems(data.map((item: any) => ({
              ...item,
              selected: false,
              qtdNF: '',
              valorNF: item.valorUnitarioSugerido != null ? String(item.valorUnitarioSugerido) : '',
            })));
          }
        } catch (error) {
          console.error('Erro ao buscar itens elegíveis do fiscal:', error);
        }
      }
    };
    fetchEligibleItems();
  }, [selectedSteelOrder]);

  if (!selectedSteelOrder) return null;

  const handleToggleItem = (index: number) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], selected: !next[index].selected };
      return next;
    });
  };

  const handleQtdChange = (index: number, value: string) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], qtdNF: value };
      return next;
    });
  };

  const handleValorChange = (index: number, value: string) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], valorNF: value };
      return next;
    });
  };

  const handleConfirm = async () => {
    const selectedItems = items.filter(item => item.selected);

    if (selectedItems.length === 0) {
      setFeedback({ message: 'Selecione ao menos um item e informe uma quantidade válida.', isOpen: true, statusOk: false });
      return;
    }

    const incompleto = selectedItems.some(item =>
      !(Number(item.qtdNF) > 0) ||
      !Number.isInteger(Number(item.qtdNF)) ||
      !(Number(item.valorNF) > 0)
    );

    if (incompleto) {
      setFeedback({ message: 'Informe quantidade inteira e valor unitário válidos para os itens selecionados.', isOpen: true, statusOk: false });
      return;
    }

    try {
      const payload = {
        orderId: selectedSteelOrder.id,
        items: selectedItems.map(item => {
          const quantidade = Math.floor(Number(item.qtdNF));
          const valorUnitario = round4(Number(item.valorNF));
          return {
            orderItemId: item.id,
            quantidade,
            valorUnitario,
            valorTotal: round4(quantidade * valorUnitario),
            ...(item.unitOfMeasurement?.id != null ? { unitOfMeasurementId: item.unitOfMeasurement.id } : {}),
          };
        }),
      };

      const { data: invoice } = await ApiService.fiscal.createNfe(payload);

      setFeedback({ message: 'Nota Fiscal gerada com sucesso!', isOpen: true, statusOk: true });
      setModalActive(false);
      navigate(`/panel/invoice/edit/${invoice.id}`);
    } catch (error) {
      console.error('❌ Erro ao gerar NF:', error);
      setFeedback({ message: 'Houve um erro ao tentar gerar a NF.', isOpen: true, statusOk: false });
    }
  };

  const companyName = typeof selectedSteelOrder.client === 'string'
    ? selectedSteelOrder.client
    : (selectedSteelOrder.client?.fantasyName || selectedSteelOrder.client?.companyName || 'Cliente Desconhecido');

  return (
    <ModalContainer>
      <Title>Pedido {selectedSteelOrder.id}</Title>
      <Subtitle>Cliente: {companyName}</Subtitle>

      <TableTitle>Itens do pedido</TableTitle>

      <Table>
        <thead>
          <tr>
            <Th>Adicionar</Th>
            <Th>Item</Th>
            <Th>NCM</Th>
            <Th>Unidade</Th>
            <Th>Valor unit.</Th>
            <Th>Qtd disponível</Th>
            <Th>Qtd para NF</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <ItemRow key={index} $inelegivel={item.elegivel === false || item.totalmenteFaturado === true}>
              <Td>
                <CheckboxInput
                  type="checkbox"
                  checked={item.selected}
                  onChange={() => handleToggleItem(index)}
                  disabled={item.elegivel === false || item.totalmenteFaturado === true}
                />
              </Td>
              <Td>
                {item.totalmenteFaturado === true ? (
                  <DescricaoRiscada>{stripHtml(item.descricao) || `Produto ${item.id || index}`}</DescricaoRiscada>
                ) : (
                  stripHtml(item.descricao) || `Produto ${item.id || index}`
                )}
                {(item.pendencias ?? []).length > 0 && (
                  <BadgesRow>
                    {item.pendencias.map((p: any) => (
                      <BadgePendencia key={p.label} $bloqueante={p.bloqueante} title={p.descricao}>
                        {p.label}
                      </BadgePendencia>
                    ))}
                  </BadgesRow>
                )}
                {item.totalmenteFaturado === true && (
                  <MotivoInelegibilidade>Item já faturado — sem saldo disponível</MotivoInelegibilidade>
                )}
              </Td>
              <Td>{item.ncm || '—'}</Td>
              <Td>{item.unitOfMeasurement?.name || '—'}</Td>
              <Td>
                {item.valorUnitarioSugerido != null ? (
                  <ValorFixo title="Valor da venda — ajustes só na edição da nota">
                    {item.valorNF}
                  </ValorFixo>
                ) : (
                  <ValorInput
                    type="number"
                    value={item.valorNF}
                    onChange={(e) => handleValorChange(index, e.target.value)}
                    disabled={!item.selected}
                    min="0"
                    step="0.0001"
                  />
                )}
              </Td>
              <Td>{item.quantidadeDisponivel}</Td>
              <Td>
                <QtdInput
                  type="number"
                  value={item.selected ? item.qtdNF : ''}
                  onChange={(e) => handleQtdChange(index, e.target.value)}
                  disabled={!item.selected}
                  min="1"
                  step="1"
                  max={item.quantidadeDisponivel}
                />
              </Td>
            </ItemRow>
          ))}
          {items.length === 0 && (
            <tr>
              <Td colSpan={7}>Nenhum item encontrado neste pedido.</Td>
            </tr>
          )}
        </tbody>
      </Table>

      {items.some((i) => (i.pendencias ?? []).length > 0) && (
        <Legenda>
          <LegendaItem><LegendaDot $bloqueante /> impede faturar</LegendaItem>
          <LegendaItem><LegendaDot /> pendência para a emissão</LegendaItem>
        </Legenda>
      )}

      <ActionsContainer>
        <CancelButton onClick={() => setModalActive(false)}>Cancelar</CancelButton>
        <ConfirmButton onClick={handleConfirm} disabled={items.filter(i => i.selected).length === 0}>Gerar NF</ConfirmButton>
      </ActionsContainer>
    </ModalContainer>
  );
};

export default GenerateOrderInvoiceModal;
